import { useEffect, useRef, useState } from 'react'

/**
 * Custom hook to debounce a value
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300)
 * @param equalityFn - Optional function to compare values for complex types
 *                    If provided, uses this function to determine if value changed
 * @param resetKey - Optional key; when it changes, the debounced value is immediately
 *                   reset to the current value and any pending flush is cancelled.
 *                   Use e.g. artifactId so that after switching context we never flush stale data.
 * @returns Debounced value
 */
export function useDebounce<T>(
	value: T,
	delay: number = 300,
	equalityFn?: (a: T, b: T) => boolean,
	resetKey?: unknown
): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value)
	const equalityFnRef = useRef<typeof equalityFn>(equalityFn)
	const lastCommittedRef = useRef<T>(value)

	useEffect(() => {
		equalityFnRef.current = equalityFn
	}, [equalityFn])

	// When resetKey changes, discard pending debounce and sync to current value.
	// biome-ignore lint/correctness/useExhaustiveDependencies: value intentionally excluded – only reset on resetKey change
	useEffect(() => {
		lastCommittedRef.current = value
		setDebouncedValue(value)
	}, [resetKey])

	useEffect(() => {
		if (equalityFnRef.current?.(value, lastCommittedRef.current)) {
			return
		}

		const handler = setTimeout(() => {
			lastCommittedRef.current = value
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}
